import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { bankAccountParseWithHolderName, boolParse, dateParser, prepareTypography } from "../ReviewParser";
import { FrequencyArraysMap, quoteSippIncomePaymentDetailsLabels } from "../../../NewQuoteWizard/Resource/Content";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { CappedAndFlexiTableReview, ColumnType } from "./CappedAndFlexiTableReview";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { ContributionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ContributionBase";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { PensionArrangementType } from "../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { nbSippIncomePaymentDetailsLabels } from "../../../NewBusinessWizard/Resource/Contents";
import { CappedModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import {
    calculateMaximumAvailablePerPayment,
    calculateMaximumGrossOneOffIncomePayment,
    calculateMaximumIncomePaymentForFlexiTarget,
    calculateMaximumIncomePaymentForFutureYear,
    calculateMaximumIncomePaymentForRemainderOfYear,
    calculateMaximumIncomePerPaymentWithStandardAlgorithm,
    calculateMaximumIncomePerPaymentWithTargetAlgorithm,
    calculateMaximumInitialIncomePayment,
    calculateGrossIncomePerPayment,
    calculateGrossIncomePerPaymentFromPension,
    frequencyMap,
} from "../../SIPP/IncomePaymentDetails/Helper/IncomePaymentHelper";
import { processTypeEnum } from "../../Resource/Contents";
import { ExistingContributionStore } from "../../../EditIncomeDrawdownWizard/Stores/Variable";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { calculateNetDrawdownValue } from "../../SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { InvestmentSelectionAction } from "../../../../../../Shared/IST/Action/InvestmentSelectionAction";
import { InstrumentSearchQuery } from "../../../../../../../Wrap/Models/Domains/Investment/InstrumentSearchQuery";
import { incomePaymentDetailsLabels } from "../../SIPP/IncomePaymentDetails/Resource/Content";

interface IIncomePaymentReviewProps {
    data: PensionBase;
    process: ProcessType;
    colNum: number;
    onEditAction: () => void;
    model: ContributionBase | ApplyModel;
    isOpen?: boolean;
    isITFC: boolean;
}

export const IncomePaymentReview = (props: IIncomePaymentReviewProps) => {
    const { data, process, isITFC } = props;
    const existingContribution = ExistingContributionStore.getExistingContribution()?.Contribution;
    const columnsTypes = {
        CappedcolumnsType: [
            { Title: "From", Key: "From", type: ColumnType.PlainText },
            { Title: "Amount", Key: "Amount", type: ColumnType.Money },
            { Title: "Type", Key: "Type", type: ColumnType.PlainText },
            { Title: "Taxable", Key: "Taxable", type: ColumnType.bool },
            { Title: "GAD Maximum", Key: "GadMaximum", type: ColumnType.Money },
            { Title: "Income taken to date", Key: "IncomeTakenToDate", type: ColumnType.Money },
            { Title: "Last mandatory review", Key: "LastMandatoryReview", type: ColumnType.DateTime },
            {
                Title: "Maximum Income Payment For Remainder Of Pension Year",
                Key: "MaximumIncomePaymentForRemainderOfPensionYear",
                type: ColumnType.Money,
            },
            {
                Title: "Maximum Income Payment For Future Pension Years",
                Key: "MaximumIncomePaymentForFuturePensionYears",
                type: ColumnType.Money,
            },
            {
                Title: "Income Per Payment For Current Pension Year",
                Key: "IncomePerPaymentForCurrentPensionYear",
                type: ColumnType.Money,
            },
            {
                Title: "Income Per Payment For Future Pension Years",
                Key: "IncomePerPaymentForFuturePensionYears",
                type: ColumnType.Money,
            },
        ],
        FlexicolumnsType: [
            { Title: "From", Key: "From", type: ColumnType.PlainText },
            { Title: "Amount", Key: "Amount", type: ColumnType.Money },
            { Title: "Type", Key: "Type", type: ColumnType.PlainText },
            { Title: "Maximum Income Per Payment", Key: "MaximumIncomePerPayment", type: ColumnType.Money },
            { Title: "Income Per Payment", Key: "IncomePerPayment", type: ColumnType.Money },
        ],
    };

    const incomePaymentConfig = (contribution: PensionBase) => {
        if (!contribution) {
            return [];
        }

        let income =
            contribution?.DrawdownModel?.Income ||
            contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income ||
            contribution?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome;
        
        if (props.process === ProcessType.TakePensionBenefits) {
            if (contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income.TargetSpecificArrangements === true
                || (contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPayment?.Amount ?? 0) > 0) {
                income = contribution.DrawdownIncomeAmendmentModel.DrawdownIncomeAmendmentInstruction.Income
            }
        }

        const incomePaymentConfig = [
            {
                title: incomePaymentDetailsLabels.needToProvideAnIncome,
                data: boolParse(income?.TaxableIncomeRequiredInAddition),
            },
            {
                title: quoteSippIncomePaymentDetailsLabels.targetSpecificArrangements,
                data: boolParse(income?.TargetSpecificArrangements),
            },
        ];

        let initialIncome = 0;

        if (income?.TargetSpecificArrangements == false) {
            if (
                data?.DrawdownModel?.DrawdownType != DrawdownType.Capped &&
                !data?.PensionTransfers?.find((transfer) => transfer.DrawdownType === DrawdownType.Capped)
            ) {
                incomePaymentConfig.push({
                    title: quoteSippIncomePaymentDetailsLabels.takeDifferentInitialIncomePayment,
                    data: boolParse(income?.TakeDifferentInitialIncomePayment),
                });
                if (income?.TakeDifferentInitialIncomePayment) {
                    incomePaymentConfig.push(
                        {
                            title: quoteSippIncomePaymentDetailsLabels.maximumInitialIncomePayment,
                            data: MoneyDisplayHelper.getMoneyFormat({
                                Amount: calculateMaximumInitialIncomePayment(process, data, isITFC),
                            }),
                        },
                        {
                            title: quoteSippIncomePaymentDetailsLabels.grossInitialIncomePayment,
                            data: MoneyDisplayHelper.getMoneyFormat(income?.GrossInitialIncomePayment),
                        }
                    );
                    initialIncome = income?.GrossInitialIncomePayment?.Amount ?? 0;
                }
            }
        }

        const frequency = contribution?.DrawdownModel?.Income?.Frequency ||
            contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.Frequency ||
            Frequency.Monthly;
        if (income?.TargetSpecificArrangements == true) {

            let pensionIncomePerPayment = 0;
            let hasTargetedPensionIncome
                = ((process == ProcessType.Quote || process == ProcessType.NB) && contribution?.PensionTransfers != null && contribution?.PensionTransfers.length > 0)
                || ((process == ProcessType.TakePensionBenefits || process == ProcessType.TopUpWithITFLS) && contribution.DrawdownModel.Income.TargetSpecificArrangements);
            initialIncome = income?.GrossInitialIncomePayment?.Amount ?? 0;
            
            if (hasTargetedPensionIncome) {
                let amount = 0;
                contribution.PensionTransfers
                    ?.filter(item => item.PensionSource == PensionArrangementType.Crystallised)
                    ?.forEach(item => {
                        amount += item.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome.AnnualAmount?.Amount.Amount ?? 0;
                    });
                pensionIncomePerPayment = amount / frequencyMap[frequency];

                pensionIncomePerPayment = (contribution.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements ?? [])
                    .reduce((sum, arrangement) => sum + (arrangement?.IncomePerPayment ?? 0), pensionIncomePerPayment);

                pensionIncomePerPayment = (contribution.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements ?? [])
                    .reduce((sum, arrangement) => sum + (arrangement?.IncomePerPaymentForCurrentPensionYear ?? 0), pensionIncomePerPayment);

                if (process == ProcessType.NB || process == ProcessType.Quote || process == ProcessType.TakePensionBenefits || process == ProcessType.TopUpWithITFLS) {
                    pensionIncomePerPayment += contribution?.DrawdownModel?.Income.GrossIncomePerPayment?.Amount ?? 0;
                }
            }

            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.maximumIncomePerPayment,
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: (calculateMaximumIncomePerPaymentWithTargetAlgorithm(process, contribution, isITFC) - initialIncome),
                }),
            });
            
            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.grossIncomePerPaymentFromPension,
                data: MoneyDisplayHelper.getMoneyFormat(
                    hasTargetedPensionIncome
                    ? { Amount:  pensionIncomePerPayment }
                    : calculateGrossIncomePerPaymentFromPension(income)),
            });
        } else {
            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.maximumIncomePerPayment,
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: (calculateMaximumIncomePerPaymentWithStandardAlgorithm(process, contribution, isITFC) - initialIncome),
                }),
            });

            incomePaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.grossIncomePerPayment,
                data: MoneyDisplayHelper.getMoneyFormat(calculateGrossIncomePerPayment(income)),
            });
        }

        incomePaymentConfig.push(
            {
                title: quoteSippIncomePaymentDetailsLabels.incomeFrequency,
                data: FrequencyArraysMap[income?.Frequency],
            },
            {
                title: quoteSippIncomePaymentDetailsLabels.firstPaymentDate,
                data: income?.NextPayment
                    ? dateParser(income?.NextPayment)
                    : null,
            },
            {
                title: "End date",
                data: income?.PaymentEnd === null
                    ? nbSippIncomePaymentDetailsLabels.untilNotified
                    : dateParser(income?.PaymentEnd),
            }
        );

        // if (process == ProcessType.NB) {
        //     incomePaymentConfig.push(
        //         {
        //             title: quoteSippIncomePaymentDetailsLabels.nextFlexiIncomeReviewDate,
        //             data: income?.FlexiIncomeReviewDate
        //                 ? dateParser(income?.FlexiIncomeReviewDate)
        //                 : null,
        //         },
        //         {
        //             title: quoteSippIncomePaymentDetailsLabels.frequencyOfFlexiIncomeReview,
        //             data: income?.FrequencyFlexiIncomeReview
        //                 ? income?.FrequencyFlexiIncomeReview + ""
        //                 : "12",
        //         }
        //     );
        // }
        // else if (process !== ProcessType.Quote && process != ProcessType.WhatIf) {
        //     incomePaymentConfig.push(
        //         {
        //             title: quoteSippIncomePaymentDetailsLabels.nextFlexiIncomeReviewDate,
        //             data: contribution.DrawdownIncomeReviewDetails?.IncomeReviewDate
        //                 ? dateParser(contribution.DrawdownIncomeReviewDetails?.IncomeReviewDate)
        //                 : null,
        //         },
        //         {
        //             title: quoteSippIncomePaymentDetailsLabels.frequencyOfFlexiIncomeReview,
        //             data: contribution.DrawdownIncomeReviewDetails?.IncomeReviewFrequencyCount
        //                 ? contribution.DrawdownIncomeReviewDetails?.IncomeReviewFrequencyCount + ""
        //                 : "12",
        //         }
        //     );
        // }
        return incomePaymentConfig;
    };

    const incomePaymentNonRequiredConfig = (contribution: PensionBase) => {
        if (!contribution) {
            return [];
        }
        
        let income =
            contribution?.DrawdownModel?.Income ||
            contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income ||
            contribution?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome;

        if (props.process === ProcessType.TakePensionBenefits) {
            if (contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income.TargetSpecificArrangements === true
                || (contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPayment?.Amount ?? 0) > 0) {
                income = contribution.DrawdownIncomeAmendmentModel.DrawdownIncomeAmendmentInstruction.Income
            }
        }

        if (income?.TaxableIncomeRequiredInAddition !== true) {
            return [
                {
                    title: incomePaymentDetailsLabels.needToProvideAnIncome,
                    data: "No",
                },
            ];
        }
        return [];
    };

    const oneoffPaymentConfig = (contribution) => {
        if (!contribution) {
            return [];
        }
        const oneOff =
            contribution?.DrawdownModel?.OneOffTaxableWithdrawalDetails ||
            contribution?.PensionTransfers[0]?.OneOffTaxableWithdrawalDetails;
        const oneOffPaymentConfig = [];
        if (oneOff != null && oneOff?.Amount?.Amount > 0) {
            oneOffPaymentConfig.push({
                title: <strong>One-off income payment</strong>,
                data: " ",
            });
            oneOffPaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.maximumGrossOneOffIncomePayment,
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: calculateMaximumGrossOneOffIncomePayment(process, contribution, isITFC),
                }),
            });
            oneOffPaymentConfig.push({
                title: quoteSippIncomePaymentDetailsLabels.oneOffIncomePayment,
                data: MoneyDisplayHelper.getMoneyFormat(oneOff.Amount),
            });
        }
        return oneOffPaymentConfig;
    };

    const regularIncomePaymentConfig = (contribution: PensionBase) => {
        if (!contribution) {
            return [];
        }
        const income =
            contribution?.DrawdownModel?.Income ||
            contribution?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome;
        const regularIncomePayment = [
            {
                title: quoteSippIncomePaymentDetailsLabels.taxableIncomeRequiredInAddition,
                data: boolParse(income?.TaxableIncomeRequiredInAddition),
            },
        ];
        if (income?.TaxableIncomeRequiredInAddition) {
            regularIncomePayment.push(
                {
                    title: quoteSippIncomePaymentDetailsLabels.maximumAvailablePerPayment,
                    data: MoneyDisplayHelper.getMoneyFormat({
                        Amount: calculateMaximumAvailablePerPayment(process, contribution?.DrawdownModel),
                    }),
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.grossIncomePerPayment,
                    data: MoneyDisplayHelper.getMoneyFormat(calculateGrossIncomePerPayment(income)),
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.incomeFrequency,
                    data: FrequencyArraysMap[contribution?.DrawdownModel?.RegularCrystallisationDetails?.Frequency],
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.firstPaymentDate,
                    data: dateParser(contribution?.DrawdownModel?.RegularCrystallisationDetails?.FirstPaymentDay),
                },
                {
                    title: quoteSippIncomePaymentDetailsLabels.endDate,
                    data: income?.PaymentEnd ? dateParser(income?.PaymentEnd) : null,
                }
            );
        }
        return regularIncomePayment;
    };

    const assetsToSellConfig = (contribution) => {
        if (!contribution) {
            return [];
        }
        const DrawdownModel = contribution?.DrawdownModel;
        var transferIsAcrossAllAssets = false;
        if (contribution?.PensionTransfers?.length > 0) {
            transferIsAcrossAllAssets = contribution?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.IsAcrossAllAssets
        }
        const assetsToSell = [];
        assetsToSell.push({
            title: <strong>Choose assets to sell</strong>,
            data: " ",
        });
        assetsToSell.push({
            title: "Choose asset selection method",
            data: (DrawdownModel?.IsAcrossAllAssets || transferIsAcrossAllAssets) ? "Proportionally across all assets" : "From specified assets",
        });

        if (DrawdownModel?.IncomeDisinvestmentAllocations?.length && !DrawdownModel?.IsAcrossAllAssets) {
            if (process == ProcessType.TakePensionBenefits) {
                const instrumentIds = DrawdownModel?.IncomeDisinvestmentAllocations.map((ele) => ele.ProductId);
                let query: InstrumentSearchQuery = {
                    CanBeSold: true,
                    ProductIds: instrumentIds,
                };
                InvestmentSelectionAction.searchInstruments(query, contribution?.OwnerHierarchyId, (result) => {
                    DrawdownModel?.IncomeDisinvestmentAllocations.forEach((instrument) => {
                        const name = result?.find((x) => {
                            return x?.ProductId === instrument.ProductId;
                        })?.Name;
                        assetsToSell.push({
                            title: <div className="pl-10">{name}</div>,
                            data: <div className="pl-10">{instrument.Percentage}%</div>,
                        });
                    });
                });
            } else {
                for (const el of DrawdownModel?.IncomeDisinvestmentAllocations) {
                    const name = contribution?.PreRetirementInstrumentSelections?.Instruments?.find((selection) => {
                        return selection?.Instrument.ProductId === el.ProductId;
                    })?.Instrument?.Name;
                    assetsToSell.push({
                        title: <div className="pl-10">{name}</div>,
                        data: <div className="pl-10">{el?.Percentage}%</div>,
                    });
                }
            }
        }
        return assetsToSell;
    };

    const bankAccountDetailsConfig = (contribution) => {
        if (!contribution) {
            return [];
        }
        const IncomePaymentAllocations =
            contribution?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.IncomePaymentAllocations ||
            contribution?.DrawdownModel?.Income?.IncomePaymentAllocations ||
            contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.IncomePaymentAllocations;
        if (!IncomePaymentAllocations) {
            return [];
        }
        const bankDetails = [];
        bankDetails.push({
            title: <strong>Bank account details</strong>,
            data: " ",
        });
        const backAccounts = [...props.model.AllBankAccounts, ...props.model.BankAccountInstructions];
        IncomePaymentAllocations.map((bankAccount) => {
            bankDetails.push({
                title: "Bank account",
                data: bankAccountParseWithHolderName(bankAccount.BankAccountId, backAccounts, bankAccount.Destination),
            });

            bankDetails.push({
                title: "Portion of PCLS",
                data: bankAccount.PclsAllocation + "%",
            });
            bankDetails.push({
                title: "Proportion of income",
                data: bankAccount.Allocation + "%",
            });
        });
        return bankDetails;
    };

    const getCappedModel = (contribution: PensionContribution): CappedModel[] => {
        const existingCappedModel =
            contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements;
        const drawdownCappedModel: CappedModel[] =
            data?.DrawdownModel?.DrawdownType == DrawdownType.Capped
                ? [
                    {
                        Type: "Member",
                        From: nbSippIncomePaymentDetailsLabels.crystallisation,
                        Amount: contribution?.DrawdownModel?.CrystallisationDetails?.CrystallisedValue?.Amount,
                        Taxable: null,
                        GadMaximum: contribution?.DrawdownModel?.GadRates?.GadMaximum,
                        IncomeTakenToDate: contribution?.DrawdownModel?.Income?.CurrentYearIncome?.Amount,
                        LastMandatoryReview: null,
                        MaximumIncomePaymentForRemainderOfPensionYear: calculateMaximumIncomePaymentForRemainderOfYear(
                            processTypeEnum.NewBusiness,
                            contribution?.PensionTransfers?.[0],
                            contribution
                        ),
                        MaximumIncomePaymentForFuturePensionYears: calculateMaximumIncomePaymentForFutureYear(
                            processTypeEnum.NewBusiness,
                            contribution?.PensionTransfers?.[0],
                            contribution
                        ),
                        IncomePerPaymentForCurrentPensionYear:
                            contribution?.PensionTransfers?.[0]?.IncomePaymentDetails?.GrossIncomePerPayment?.Amount,
                        IncomePerPaymentForFuturePensionYears:
                            contribution?.PensionTransfers?.[0]?.IncomePaymentDetails?.FutureYearIncome?.Amount,
                    },
                ]
                : [];
        const pensionTransferCapped: CappedModel[] = (contribution?.PensionTransfers || [])
            .filter(
                (ele) =>
                    ele.DrawdownType == DrawdownType.Capped && ele.PensionSource == PensionArrangementType.Crystallised
            )
            .map((ele) => {
                return {
                    Type: ele?.IsInheritedFund ? "Inherited" : "Member",
                    From: ele?.ProviderDetails?.Name,
                    Amount: ele?.CurrentValue?.Amount,
                    Taxable: null,
                    GadMaximum: ele?.IncomePaymentDetails?.GadMaximum?.Amount,
                    IncomeTakenToDate: ele?.IncomePaymentDetails?.CurrentYearIncome?.Amount,
                    LastMandatoryReview: ele?.IncomePaymentDetails?.LastGadReviewDate,
                    MaximumIncomePaymentForRemainderOfPensionYear: calculateMaximumIncomePaymentForRemainderOfYear(
                        processTypeEnum.NewBusiness,
                        ele,
                        contribution
                    ),
                    MaximumIncomePaymentForFuturePensionYears: calculateMaximumIncomePaymentForFutureYear(
                        processTypeEnum.NewBusiness,
                        ele,
                        contribution
                    ),
                    IncomePerPaymentForCurrentPensionYear: ele?.IncomePaymentDetails?.GrossIncomePerPayment?.Amount,
                    IncomePerPaymentForFuturePensionYears: ele?.IncomePaymentDetails?.FutureYearIncome?.Amount,
                };
            });
        return drawdownCappedModel.concat(pensionTransferCapped, existingCappedModel);
    };

    const getFlexiModel = (contribution: PensionContribution): FlexiModel[] => {
        const { process } = props;
        const existingFlexiModel =
            contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements;
        const drawdownFlexiModel: FlexiModel[] =
            data?.DrawdownModel?.DrawdownType == DrawdownType.Flexible
                ? [
                    {
                        Type: "Member",
                        From: nbSippIncomePaymentDetailsLabels.crystallisation,
                        Amount: calculateNetDrawdownValue(data?.DrawdownModel, data?.PensionProtection),
                        MaximumIncomePerPayment: calculateMaximumIncomePaymentForFlexiTarget(
                            process,
                            null,
                            data,
                            true
                        ),
                        IncomePerPayment: getFlexiIncomePerPaymentFromDrawdownModel(contribution),
                    },
                ]
                : [];
        const pensionTransferFlexi = (contribution?.PensionTransfers || [])
            .filter(
                (ele) =>
                    ele.DrawdownType == DrawdownType.Flexible &&
                    ele.PensionSource == PensionArrangementType.Crystallised
            )
            .map((ele) => {
                return {
                    Type: ele?.IsInheritedFund ? "Inherited" : "Member",
                    From: ele?.ProviderDetails?.Name,
                    Amount: ele?.CurrentValue?.Amount,
                    MaximumIncomePerPayment: calculateMaximumIncomePaymentForFlexiTarget(
                        processTypeEnum.NewBusiness,
                        ele,
                        contribution,
                        isITFC
                    ),
                    IncomePerPayment: ele?.IncomePaymentDetails?.GrossIncomePerPayment?.Amount,
                };
            });
        return drawdownFlexiModel.concat(pensionTransferFlexi, existingFlexiModel);
    };

    const getFlexiIncomePerPaymentFromDrawdownModel = (contribution: PensionContribution): number => {
        const income = contribution?.DrawdownModel?.Income;
        return income?.TargetSpecificArrangements
            ? (income?.AnnualAmount?.Amount?.Amount ?? 0) / frequencyMap[income?.Frequency]
            : income?.GrossIncomePerPayment?.Amount;
    };

    const incomeNotRequiredConfig = incomePaymentNonRequiredConfig(data);

    return (
        <Accordion label="Income payment details" isOpen={false} onEdit={props.onEditAction}>
            {
            process == ProcessType.TakePensionBenefits && incomeNotRequiredConfig.length > 0
                ? (
                    <div className="row">
                        <div className="col-12 col-sm-fluid">
                        <Typography {...prepareTypography(incomeNotRequiredConfig, 1, "common")} />
                        </div>
                    </div>
                )
                : (
                <>
                    {process !== ProcessType.WhatIf && (
                    <div className="row">
                        <div className="col-12 col-sm-fluid">
                        <h3>Assets to sell</h3>
                        <Typography {...prepareTypography(assetsToSellConfig(data), 1, "common")}></Typography>
                        </div>
                    </div>
                    )}

                    <div className="row">
                    <div className="col-12 col-sm-fluid">
                        <h3>Payment details</h3>
                        {data?.DrawdownModel?.DrawdownType == DrawdownType.Regular ? (
                        <div className="pl-34">
                            <Typography
                                {...prepareTypography(regularIncomePaymentConfig(data), 1, "common")}
                            ></Typography>
                            <br />
                            <Typography {...prepareTypography(oneoffPaymentConfig(data), 1, "common")}></Typography>
                        </div>
                        ) : (
                        <div className="pl-34">
                            <Typography {...prepareTypography(incomePaymentConfig(data), 1, "common")}></Typography>
                            <br />
                            <Typography {...prepareTypography(oneoffPaymentConfig(data), 1, "common")}></Typography>
                        </div>
                        )}

                        {(process == ProcessType.NB || process == ProcessType.NBEC) && (
                            <>
                                <div className="pl-34">
                                    <Typography {...prepareTypography(bankAccountDetailsConfig(data), 1, "common")} />
                                </div>
                            </>
                        )}
                    </div>
                    </div>
                </>
                )
            }
            {process != ProcessType.NB && process != ProcessType.NBEC && process != ProcessType.Quote && (
                <>
                <div className="row">
                    <div className="col-12 col-sm-fluid">
                        <h3>Before</h3>
                        {(data?.DrawdownModel?.Income?.TargetSpecificArrangements || data?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements) &&
                            existingContribution != null && (
                                <div className="pl-34">
                                    {/* {getCappedModel(existingContribution).length > 0 && (
                                        <CappedAndFlexiTableReview
                                            tableTitle="Capped"
                                            data={getCappedModel(existingContribution)}
                                            columnsType={columnsTypes.CappedcolumnsType}
                                        />
                                    )}
                                    {getFlexiModel(existingContribution).length > 0 && (
                                        <CappedAndFlexiTableReview
                                            tableTitle={"Flexi"}
                                            data={getFlexiModel(existingContribution)}
                                            columnsType={columnsTypes.FlexicolumnsType}
                                        />
                                    )} */}
                                </div>
                            )}

                            <>
                                <div className="pl-34">
                                    <Typography
                                        {...prepareTypography(bankAccountDetailsConfig(existingContribution), 1, "common")}
                                    />
                                </div>
                            </>
                    </div>
                    <div className="col-12 col-sm-fluid">
                        <h3>After</h3>
                        {(data?.DrawdownModel?.Income?.TargetSpecificArrangements || data?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements) && (
                            <div className="pl-34">
                                {/* {getCappedModel(data).length > 0 && (
                                    <CappedAndFlexiTableReview
                                        tableTitle="Capped"
                                        data={getCappedModel(data)}
                                        columnsType={columnsTypes.CappedcolumnsType}
                                    />
                                )} */}
                                {getFlexiModel(data).length > 0 && (
                                    <CappedAndFlexiTableReview
                                        tableTitle={"Flexi"}
                                        data={getFlexiModel(data)}
                                        columnsType={columnsTypes.FlexicolumnsType}
                                    />
                                )}
                            </div>
                        )}

                            <>
                                <div className="pl-34">
                                    <Typography {...prepareTypography(bankAccountDetailsConfig(data), 1, "common")} />
                                </div>
                            </>
                    </div>
                </div>
                </>
            )}
        </Accordion>
    );
};
